// Coordinates community-feed video playback. Two jobs:
//
// 1. One-at-a-time: as the user scrolls, only the single MOST-visible feed
//    video plays; every other registered player is told to pause. Players
//    report their IntersectionObserver ratio here and get an `onActiveChange`
//    callback when they become (or stop being) the active video.
//
// 2. Sound preference: videos should start WITH sound, but browsers/WebViews
//    block autoplay-with-sound until the user has interacted with the page.
//    So we (a) remember the user's mute choice across videos (persisted), and
//    (b) expose a one-shot "first gesture" hook so a muted-fallback video can
//    unmute itself the moment that becomes allowed.

// Key deliberately bumped from the original `feedVideoMuted`. A blocked
// unmuted-autoplay used to be persisted as though the user had chosen to mute,
// so devices are carrying a stored `true` nobody asked for — and every video
// started silent because of it. Reading a new key retires those values once;
// choices made from here on are genuine.
const MUTE_KEY = 'feedVideoMuted.v2'

// ── Sound (mute) preference — default: sound ON ──────────────────────────────
let mutedPref = (() => {
	try {
		const v = localStorage.getItem(MUTE_KEY)
		return v === null ? false : v === 'true'
	} catch {
		return false
	}
})()
const muteSubs = new Set()

export const getFeedMuted = () => mutedPref
export const setFeedMuted = (val) => {
	const next = !!val
	if (next === mutedPref) return
	mutedPref = next
	try {
		localStorage.setItem(MUTE_KEY, String(next))
	} catch {
		/* storage unavailable — keep the in-memory value */
	}
	muteSubs.forEach((fn) => {
		try {
			fn(next)
		} catch {
			/* ignore subscriber errors */
		}
	})
}
// Apply the user's EXPLICIT mute choice: persist it AND always re-broadcast to
// every on-screen player, even when the stored pref is unchanged. Autoplay
// policy can leave sibling videos muted while the pref already says sound-on, so
// a plain setFeedMuted(false) would short-circuit (next === mutedPref) and never
// tell the other videos to unmute. This one never short-circuits.
export const setFeedMutedFromUser = (val) => {
	const next = !!val
	mutedPref = next
	try {
		localStorage.setItem(MUTE_KEY, String(next))
	} catch {
		/* storage unavailable — keep the in-memory value */
	}
	muteSubs.forEach((fn) => {
		try {
			fn(next)
		} catch {
			/* ignore subscriber errors */
		}
	})
}
export const subscribeFeedMuted = (fn) => {
	muteSubs.add(fn)
	return () => muteSubs.delete(fn)
}

// ── One-at-a-time visibility coordinator ─────────────────────────────────────
// A video must be at least half on-screen to be eligible; among the eligible,
// the most-visible wins. Matches the player's existing 0.5 visibility gate.
const MIN_RATIO = 0.5
const players = new Map() // id -> { ratio, onActiveChange }
let activeId = null

const recompute = () => {
	let bestId = null
	let best = -1
	players.forEach((p, id) => {
		if (p.ratio >= MIN_RATIO && p.ratio > best) {
			best = p.ratio
			bestId = id
		}
	})
	if (bestId === activeId) return
	const prev = activeId
	activeId = bestId
	if (prev) players.get(prev)?.onActiveChange(false)
	if (activeId) players.get(activeId)?.onActiveChange(true)
}

export const registerFeedVideo = (id, onActiveChange) => {
	players.set(id, { ratio: 0, onActiveChange })
	return () => {
		players.delete(id)
		if (activeId === id) {
			activeId = null
			recompute()
		}
	}
}
export const reportFeedVideoVisibility = (id, ratio) => {
	const p = players.get(id)
	if (!p) return
	p.ratio = ratio
	recompute()
}
export const clearFeedVideoVisibility = (id) => {
	const p = players.get(id)
	if (!p) return
	p.ratio = 0
	recompute()
}

// ── First-gesture hook (unlocks unmuted playback) ────────────────────────────
let gestured = false
const gestureSubs = new Set()
const GESTURE_EVENTS = ['pointerdown', 'touchstart', 'keydown']

const handleGesture = () => {
	if (gestured) return
	gestured = true
	gestureSubs.forEach((fn) => {
		try {
			fn()
		} catch {
			/* ignore subscriber errors */
		}
	})
	gestureSubs.clear()
	GESTURE_EVENTS.forEach((e) =>
		window.removeEventListener(e, handleGesture, { capture: true }),
	)
}

if (typeof window !== 'undefined') {
	GESTURE_EVENTS.forEach((e) =>
		window.addEventListener(e, handleGesture, { passive: true, capture: true }),
	)
}

export const hasUserGesture = () => gestured
// Runs `fn` immediately if the user has already interacted, otherwise on the
// next interaction. Returns an unsubscribe fn for the pending case.
export const whenUserGesture = (fn) => {
	if (gestured) {
		fn()
		return () => {}
	}
	gestureSubs.add(fn)
	return () => gestureSubs.delete(fn)
}
